import React, { useState, useMemo, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import CloseIcon from '@mui/icons-material/Close'
import useAccount from '../hooks/useAccount'
import useSnackbar from '../hooks/useSnackbar'
import VisiblePropertiesControls from '../components/platinum-table-new/VisiblePropertiesControls'
import PlatinumTableWrapper from '../components/platinum-table-new/PlatinumTableWrapper'
import Tree from '../components/tree/Tree'
import ParityCheckExperiment from '../components/developer/ParityCheckExperiment'
import LongTopHeaderMock from '../components/developer/LongTopHeaderMock'
import { TableVisibilitySettings } from '../types/app'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { GenNode } from '../types/carbon/models/GenNode'
import { getNodeID } from '../utils/nodeTypes'
import { andFilters } from '../utils/filter'

const CARBON_BASE_URL = 'https://bayesprice.azurewebsites.net/carbontest'

interface HttpRequest {
  id: number
  method: 'GET' | 'POST'
  endpoint: string
  requestBody?: unknown
  responseBody?: unknown
  statusCode: number
  durationMs: number
  error?: string
}

interface TableLoaderExample {
  name: string
  customer: string
  job: string
  tablePath: string
  filter: string
  visibility: TableVisibilitySettings
}

interface RunSpecExample {
  name: string
  customer: string
  job: string
  tablePath: string
}


// TODO: Add your examples here - each example pre-fills all form fields
const TABLE_LOADER_EXAMPLES: TableLoaderExample[] = [
  {
    name: 'Multi-table filter leads to zeros',
    customer: 'bprtesting',
    job: 'prius-testing',
    tablePath: 'Tables/Exec/Standard Tables/Broadband/B Gender',
    filter: 'ageband2(1)',
    visibility: { frequency: false, column_percent: true, row_percent: false, significance: false },
  },
]

// RunSpec examples - simpler, just need customer/job/tablePath for timing tests
const RUN_SPEC_EXAMPLES: RunSpecExample[] = [
  {
    name: 'Run spec timings',
    customer: 'bprtesting',
    job: 'prius-testing',
    tablePath: 'Tables/Exec/Folder/GenAge2',
  },
]

const Developer: React.FC = () => {
  const account = useAccount()
  const snackbar = useSnackbar()

  // Get all customers from session info
  const customers = account.user?.session_info?.sessionCusts || []

  // Initialize with first customer/job
  const firstCustomer = customers[0]?.name || ''
  const firstJob = customers[0]?.sessionJobs?.[0]?.name || ''

  // Local state for session context (independent of global context)
  const [sessionId, setSessionId] = useState(account.user?.session_info?.sessionId || '')
  const [selectedCustomer, setSelectedCustomer] = useState(firstCustomer)
  const [selectedJob, setSelectedJob] = useState(firstJob)

  // Get jobs for the selected customer
  const jobs = useMemo(() => {
    const customer = customers.find(c => c.name === selectedCustomer)
    return customer?.sessionJobs || []
  }, [customers, selectedCustomer])

  // Table Loader with Filter state
  const [tablePath, setTablePath] = useState('')
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [requests, setRequests] = useState<HttpRequest[]>([])
  const [platinumData, setPlatinumData] = useState<PlatinumData | null>(null)
  const [visibility, setVisibility] = useState<TableVisibilitySettings>({
    frequency: false,
    column_percent: true,
    row_percent: false,
    significance: false,
  })
  const [tableBrowserOpen, setTableBrowserOpen] = useState(false)
  const [tableBrowserExpanded, setTableBrowserExpanded] = useState<string[]>([])
  const [browserToc, setBrowserToc] = useState<GenNode[]>([])
  const [browserTocLoading, setBrowserTocLoading] = useState(false)

  // Ref to track unique request IDs (avoids stale closure issues)
  const requestIdRef = useRef(0)

  // RunSpec experiment state
  const [runSpecTablePath, setRunSpecTablePath] = useState('')
  const [runSpecLoading, setRunSpecLoading] = useState(false)
  const [runSpecError, setRunSpecError] = useState<string | null>(null)
  const [runSpecRequests, setRunSpecRequests] = useState<HttpRequest[]>([])
  const [runSpecPlatinumData, setRunSpecPlatinumData] = useState<PlatinumData | null>(null)
  const [runSpecBrowserOpen, setRunSpecBrowserOpen] = useState(false)
  const [runSpecBrowserExpanded, setRunSpecBrowserExpanded] = useState<string[]>([])
  const runSpecRequestIdRef = useRef(0)

  // Copy to clipboard helper
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      snackbar.success(`${label} copied to clipboard`)
    } catch (err) {
      snackbar.error('Failed to copy to clipboard')
    }
  }

  // Fetch TOC for selected customer/job when opening table browser
  const openTableBrowser = async (forRunSpec: boolean) => {
    if (!selectedCustomer || !selectedJob || !sessionId) {
      snackbar.error('Please select a Customer and Job first')
      return
    }

    if (forRunSpec) {
      setRunSpecBrowserOpen(true)
    } else {
      setTableBrowserOpen(true)
    }
    setBrowserTocLoading(true)
    setBrowserToc([])
    setTableBrowserExpanded([])
    setRunSpecBrowserExpanded([])

    try {
      const response = await fetch(`${CARBON_BASE_URL}/job/open`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-session-id': sessionId,
        },
        body: JSON.stringify({
          customerName: selectedCustomer,
          jobName: selectedJob,
          vartreeName: 'VarTree',
          getDisplayProps: true,
          getVartreeNames: true,
          getAxisTreeNames: true,
          tocType: 'SIMPLE',
          getDrills: false,
        }),
      })

      if (!response.ok) {
        throw new Error(`Failed to open job: ${response.status}`)
      }

      const data = await response.json() as { toc?: GenNode[] }
      setBrowserToc(data.toc || [])
    } catch (err) {
      snackbar.error(err instanceof Error ? err.message : 'Failed to load table list')
      if (forRunSpec) {
        setRunSpecBrowserOpen(false)
      } else {
        setTableBrowserOpen(false)
      }
    } finally {
      setBrowserTocLoading(false)
    }
  }

  // Carbon fetch that automatically tracks HTTP requests
  const carbonFetch = async (
    endpoint: string,
    options?: RequestInit
  ): Promise<unknown> => {
    const method = (options?.method || 'GET') as 'GET' | 'POST'
    const requestBody = options?.body ? JSON.parse(options.body as string) : undefined

    // Generate unique ID for this request (avoids stale closure issues)
    const requestId = ++requestIdRef.current

    // Add pending request
    const pendingRequest: HttpRequest = {
      id: requestId,
      method,
      endpoint,
      requestBody,
      statusCode: 0,
      durationMs: 0,
    }
    setRequests(prev => [...prev, pendingRequest])

    const startTime = performance.now()
    const url = `${CARBON_BASE_URL}/${endpoint}`

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-session-id': sessionId,
          ...options?.headers,
        },
      })

      const durationMs = Math.round(performance.now() - startTime)
      const responseBody = await response.json()

      // Update request by ID (not index)
      setRequests(prev => prev.map(req =>
        req.id === requestId
          ? { ...req, responseBody, statusCode: response.status, durationMs }
          : req
      ))

      if (!response.ok) {
        throw new Error(`Carbon API error: ${response.status} ${response.statusText}`)
      }

      return responseBody
    } catch (err) {
      const durationMs = Math.round(performance.now() - startTime)
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'

      // Update request by ID (not index)
      setRequests(prev => prev.map(req =>
        req.id === requestId
          ? { ...req, statusCode: 0, durationMs, error: errorMessage }
          : req
      ))

      throw err
    }
  }

  // Carbon fetch for RunSpec experiment (uses separate state)
  const carbonFetchRunSpec = async (
    endpoint: string,
    options?: RequestInit
  ): Promise<unknown> => {
    const method = (options?.method || 'GET') as 'GET' | 'POST'
    const requestBody = options?.body ? JSON.parse(options.body as string) : undefined

    // Generate unique ID for this request
    const requestId = ++runSpecRequestIdRef.current

    // Add pending request
    const pendingRequest: HttpRequest = {
      id: requestId,
      method,
      endpoint,
      requestBody,
      statusCode: 0,
      durationMs: 0,
    }
    setRunSpecRequests(prev => [...prev, pendingRequest])

    const startTime = performance.now()
    const url = `${CARBON_BASE_URL}/${endpoint}`

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-session-id': sessionId,
          ...options?.headers,
        },
      })

      const durationMs = Math.round(performance.now() - startTime)
      const responseBody = await response.json()

      // Update request by ID
      setRunSpecRequests(prev => prev.map(req =>
        req.id === requestId
          ? { ...req, responseBody, statusCode: response.status, durationMs }
          : req
      ))

      if (!response.ok) {
        throw new Error(`Carbon API error: ${response.status} ${response.statusText}`)
      }

      return responseBody
    } catch (err) {
      const durationMs = Math.round(performance.now() - startTime)
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'

      // Update request by ID
      setRunSpecRequests(prev => prev.map(req =>
        req.id === requestId
          ? { ...req, statusCode: 0, durationMs, error: errorMessage }
          : req
      ))

      throw err
    }
  }

  const handleExecute = async () => {
    if (!selectedCustomer || !selectedJob) {
      setError('Please select a Customer and Job')
      return
    }

    if (!tablePath.trim()) {
      setError('Table path is required')
      return
    }

    if (!sessionId) {
      setError('No session ID available. Please ensure you are logged in.')
      return
    }

    setLoading(true)
    setError(null)
    setRequests([])
    setPlatinumData(null)
    requestIdRef.current = 0

    // Decode URL-encoded path (in case user copied from URL)
    const decodedPath = decodeURIComponent(tablePath.trim())

    try {
      // Step 1: Open Job (required before any table operations)
      await carbonFetch('job/open', {
        method: 'POST',
        body: JSON.stringify({
          customerName: selectedCustomer,
          jobName: selectedJob,
          vartreeName: 'VarTree',
          getDisplayProps: true,
          getVartreeNames: true,
          getAxisTreeNames: true,
          tocType: 'SIMPLE',
          getDrills: false,
        }),
      })

      // Step 2: Load table
      await carbonFetch('report/load', {
        method: 'POST',
        body: JSON.stringify({ name: decodedPath }),
      })

      // Step 3: Get edit spec to find existing filter
      const spec = await carbonFetch('report/spec/edit') as { spec?: { filter?: string } }
      const existingFilter = spec.spec?.filter || ''

      // Combine filters (local computation, not an HTTP request)
      const combinedFilter = andFilters(existingFilter, filter)

      // Step 4: Quick update with visibility + filter
      await carbonFetch('report/quickupdate/platinum', {
        method: 'POST',
        body: JSON.stringify({
          filter: combinedFilter,
          showFreq: visibility.frequency,
          showColPct: visibility.column_percent,
          showRowPct: visibility.row_percent,
          showSig: visibility.significance,
        }),
      })

      // Step 5: Get platinum data
      const result = await carbonFetch('report/platinum') as PlatinumData
      setPlatinumData(result)

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleRunSpec = async () => {
    if (!selectedCustomer || !selectedJob) {
      setRunSpecError('Please select a Customer and Job')
      return
    }

    if (!runSpecTablePath.trim()) {
      setRunSpecError('Table path is required')
      return
    }

    if (!sessionId) {
      setRunSpecError('No session ID available. Please ensure you are logged in.')
      return
    }

    setRunSpecLoading(true)
    setRunSpecError(null)
    setRunSpecRequests([])
    setRunSpecPlatinumData(null)
    runSpecRequestIdRef.current = 0

    // Decode URL-encoded path
    const decodedPath = decodeURIComponent(runSpecTablePath.trim())

    try {
      // Step 1: Open Job
      await carbonFetchRunSpec('job/open', {
        method: 'POST',
        body: JSON.stringify({
          customerName: selectedCustomer,
          jobName: selectedJob,
          vartreeName: 'VarTree',
          getDisplayProps: true,
          getVartreeNames: true,
          getAxisTreeNames: true,
          tocType: 'SIMPLE',
          getDrills: false,
        }),
      })

      // Step 2: Load table
      await carbonFetchRunSpec('report/load', {
        method: 'POST',
        body: JSON.stringify({ name: decodedPath }),
      })

      // Step 3: Get edit spec
      const specResponse = await carbonFetchRunSpec('report/spec/edit') as { spec?: unknown }
      const tableSpec = specResponse.spec

      // Step 4: Run spec directly (bypasses quickupdate)
      const result = await carbonFetchRunSpec('report/spec/run/platinum', {
        method: 'POST',
        body: JSON.stringify({
          name: 'Developer RunSpec Test',
          pProps: {},  // Empty - use defaults
          spec: tableSpec,
        }),
      }) as PlatinumData

      setRunSpecPlatinumData(result)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setRunSpecError(message)
    } finally {
      setRunSpecLoading(false)
    }
  }

  const getStatusColor = (req: HttpRequest): 'success' | 'error' | 'default' => {
    if (req.error || req.statusCode >= 400) return 'error'
    if (req.statusCode >= 200 && req.statusCode < 300) return 'success'
    return 'default'
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Developer Tools</Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Open Chrome DevTools to see raw HTTP requests: <strong>F12</strong> or <strong>Ctrl+Shift+I</strong> (Windows/Linux) / <strong>Cmd+Option+I</strong> (Mac), then select the <strong>Network</strong> tab.
        </Typography>
      </Alert>

      {/* Session Context - shared across all experiments */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Session ID"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder="Enter session ID"
            size="small"
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={selectedCustomer}
              label="Customer"
              onChange={(e) => {
                const newCustomer = e.target.value
                setSelectedCustomer(newCustomer)
                // Set first job for new customer
                const customer = customers.find(c => c.name === newCustomer)
                setSelectedJob(customer?.sessionJobs?.[0]?.name || '')
              }}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.name || ''} value={customer.name || ''}>
                  {customer.displayName || customer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }} disabled={!selectedCustomer}>
            <InputLabel>Job</InputLabel>
            <Select
              value={selectedJob}
              label="Job"
              onChange={(e) => setSelectedJob(e.target.value)}
            >
              {jobs.map((job) => (
                <MenuItem key={job.name || ''} value={job.name || ''}>
                  {job.displayName || job.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Experiment 1: Table Loader with Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Table Loader with Filter</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Replicates the table loading flow from table_loader.go, but calls Carbon directly from the browser.
          All requests are visible in Chrome DevTools Network tab.
        </Typography>

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Table Path"
              placeholder="e.g., Tables/Standard/MyTable"
              value={tablePath}
              onChange={(e) => setTablePath(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              helperText="URL-encoded paths are automatically decoded"
            />
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => openTableBrowser(false)}
              sx={{ mt: '2px' }}
            >
              Browse
            </Button>
          </Stack>

          <TextField
            label="Filter (Multi-table filter to combine)"
            placeholder="e.g., Gender{Male}"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
            size="small"
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Visibility:
            </Typography>
            <VisiblePropertiesControls
              viewSettings={visibility}
              onViewSettingsChange={(settings) => setVisibility(prev => ({ ...prev, ...settings }))}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Load Example</InputLabel>
              <Select
                value=""
                label="Load Example"
                onChange={(e) => {
                  const example = TABLE_LOADER_EXAMPLES.find(ex => ex.name === e.target.value)
                  if (example) {
                    setSelectedCustomer(example.customer)
                    setSelectedJob(example.job)
                    setTablePath(example.tablePath)
                    setFilter(example.filter)
                    setVisibility(example.visibility)
                  }
                }}
              >
                {TABLE_LOADER_EXAMPLES.map((example) => (
                  <MenuItem key={example.name} value={example.name}>
                    {example.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="info"
              onClick={handleExecute}
              disabled={loading || !sessionId}
            >
              {loading ? 'Executing...' : 'Execute'}
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {requests.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>HTTP Requests:</Typography>
            {requests.map((req, index) => (
              <Accordion key={index} defaultExpanded={!!req.error}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Chip
                      label={req.method}
                      size="small"
                      color="default"
                      sx={{ minWidth: 60 }}
                    />
                    <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace' }}>
                      /{req.endpoint}
                    </Typography>
                    {req.statusCode > 0 ? (
                      <>
                        <Chip
                          label={req.statusCode}
                          size="small"
                          color={getStatusColor(req)}
                          sx={{ minWidth: 50 }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, textAlign: 'right' }}>
                          {req.durationMs}ms
                        </Typography>
                      </>
                    ) : req.error ? (
                      <Chip label="Error" size="small" color="error" />
                    ) : (
                      <CircularProgress size={16} />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={2}>
                    {/* Left 50% - Request Body */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {req.requestBody !== undefined ? (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Request Body:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              p: 1,
                              borderRadius: 1,
                              overflow: 'auto',
                              fontSize: '0.75rem',
                              maxHeight: 300,
                              mt: 0.5,
                            }}
                          >
                            {JSON.stringify(req.requestBody, null, 2)}
                          </Box>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copyToClipboard(JSON.stringify(req.requestBody, null, 2), 'Request')}
                            sx={{ mt: 0.5 }}
                          >
                            Copy
                          </Button>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No request body (GET request)
                        </Typography>
                      )}
                    </Box>

                    {/* Right 50% - Response Body / Error */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {req.error !== undefined && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                            Error:
                          </Typography>
                          <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                            {req.error}
                          </Typography>
                        </Box>
                      )}
                      {req.responseBody !== undefined && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Response Body:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              backgroundColor: '#e8f5e9',
                              p: 1,
                              borderRadius: 1,
                              overflow: 'auto',
                              fontSize: '0.75rem',
                              maxHeight: 300,
                              mt: 0.5,
                            }}
                          >
                            {JSON.stringify(req.responseBody, null, 2)}
                          </Box>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copyToClipboard(JSON.stringify(req.responseBody, null, 2), 'Response')}
                            sx={{ mt: 0.5 }}
                          >
                            Copy
                          </Button>
                        </>
                      )}
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {platinumData && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Result:</Typography>
            <PlatinumTableWrapper
              data={platinumData}
              hideControls={true}
            />
          </Box>
        )}
      </Paper>

      {/* Experiment 2: RunSpec */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>RunSpec</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Runs a table spec directly via report/spec/run/platinum.
          Shows timing for each Carbon API call.
        </Typography>

        

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Table Path"
              placeholder="e.g., Tables/Standard/MyTable"
              value={runSpecTablePath}
              onChange={(e) => setRunSpecTablePath(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              helperText="URL-encoded paths are automatically decoded"
            />
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => openTableBrowser(true)}
              sx={{ mt: '2px' }}
            >
              Browse
            </Button>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
              <InputLabel>Load Example</InputLabel>
              <Select
                label="Load Example"
                onChange={(e) => {
                  const example = RUN_SPEC_EXAMPLES.find(ex => ex.name === e.target.value)
                  if (example) {
                    setSelectedCustomer(example.customer)
                    setSelectedJob(example.job)
                    setRunSpecTablePath(example.tablePath)
                  }
                }}
              >
                {RUN_SPEC_EXAMPLES.map((example) => (
                  <MenuItem key={example.name} value={example.name}>
                    {example.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="info"
              onClick={handleRunSpec}
              disabled={runSpecLoading || !sessionId}
              sx={{ alignSelf: 'flex-start' }}
            >
              {runSpecLoading ? 'Running...' : 'Run Spec'}
            </Button>
          </Stack>
        </Stack>

        {runSpecError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {runSpecError}
          </Alert>
        )}

        {runSpecRequests.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>HTTP Requests:</Typography>
            {runSpecRequests.map((req, index) => (
              <Accordion key={index} defaultExpanded={!!req.error}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Chip
                      label={req.method}
                      size="small"
                      color="default"
                      sx={{ minWidth: 60 }}
                    />
                    <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace' }}>
                      /{req.endpoint}
                    </Typography>
                    {req.statusCode > 0 ? (
                      <>
                        <Chip
                          label={req.statusCode}
                          size="small"
                          color={getStatusColor(req)}
                          sx={{ minWidth: 50 }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, textAlign: 'right' }}>
                          {req.durationMs}ms
                        </Typography>
                      </>
                    ) : req.error ? (
                      <Chip label="Error" size="small" color="error" />
                    ) : (
                      <CircularProgress size={16} />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={2}>
                    {/* Left 50% - Request Body */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {req.requestBody !== undefined ? (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Request Body:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              p: 1,
                              borderRadius: 1,
                              overflow: 'auto',
                              fontSize: '0.75rem',
                              maxHeight: 300,
                              mt: 0.5,
                            }}
                          >
                            {JSON.stringify(req.requestBody, null, 2)}
                          </Box>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copyToClipboard(JSON.stringify(req.requestBody, null, 2), 'Request')}
                            sx={{ mt: 0.5 }}
                          >
                            Copy
                          </Button>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No request body (GET request)
                        </Typography>
                      )}
                    </Box>

                    {/* Right 50% - Response Body / Error */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {req.error !== undefined && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                            Error:
                          </Typography>
                          <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                            {req.error}
                          </Typography>
                        </Box>
                      )}
                      {req.responseBody !== undefined && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Response Body:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              backgroundColor: '#e8f5e9',
                              p: 1,
                              borderRadius: 1,
                              overflow: 'auto',
                              fontSize: '0.75rem',
                              maxHeight: 300,
                              mt: 0.5,
                            }}
                          >
                            {JSON.stringify(req.responseBody, null, 2)}
                          </Box>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copyToClipboard(JSON.stringify(req.responseBody, null, 2), 'Response')}
                            sx={{ mt: 0.5 }}
                          >
                            Copy
                          </Button>
                        </>
                      )}
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {runSpecPlatinumData && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Timing:</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                {[
                  { label: 'Prepare', value: runSpecPlatinumData.prepareSecs },
                  { label: 'Data', value: runSpecPlatinumData.dataSecs },
                  { label: 'Endpoint', value: runSpecPlatinumData.endpointSecs },
                  { label: 'Raw Tabs', value: runSpecPlatinumData.carbonRawTabsSecs, },
                  { label: 'Gen Tabs', value: runSpecPlatinumData.carbonGenTabsSecs },
                  { label: 'Format', value: runSpecPlatinumData.carbonFormatSecs },
                ].map(({ label, value }) => (
                  <Box key={label} sx={{ textAlign: 'center', minWidth: 80 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {label}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" fontFamily="monospace">
                      {value != null ? `${(value * 1000).toFixed(0)}ms` : '-'}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Table:</Typography>
            <PlatinumTableWrapper
              data={runSpecPlatinumData}
              hideControls={true}
            />
          </Box>
        )}
      </Paper>

      {/* Experiment 3: Carbon-Go Parity Check */}
      <ParityCheckExperiment />

      {/* Experiment 4: Long Top Header Mock */}
      <LongTopHeaderMock />

      {/* Table Browser Dialog for Table Loader experiment */}
      <Dialog
        open={tableBrowserOpen}
        onClose={() => setTableBrowserOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Table
          <IconButton
            onClick={() => setTableBrowserOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400 }}>
          {browserTocLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <CircularProgress />
            </Box>
          ) : browserToc.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No tables found for {selectedCustomer}/{selectedJob}
            </Typography>
          ) : (
            <Tree
              searchEnabled={true}
              nodes={browserToc}
              expanded={tableBrowserExpanded}
              selected=""
              onNodeExpandToggle={(itemId: string, node: GenNode, isExpanded: boolean) => {
                setTableBrowserExpanded(prev =>
                  isExpanded
                    ? [...prev, itemId]
                    : prev.filter(id => id !== itemId)
                )
              }}
              onNodeSelectToggle={(itemId: string, node: GenNode, isSelected: boolean) => {
                if (isSelected && node.type === 'Table') {
                  const fullPath = getNodeID(node)
                  // Remove .cbt extension if present
                  setTablePath(fullPath.replace(/\.cbt$/i, ''))
                  setTableBrowserOpen(false)
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Table Browser Dialog for RunSpec experiment */}
      <Dialog
        open={runSpecBrowserOpen}
        onClose={() => setRunSpecBrowserOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Table
          <IconButton
            onClick={() => setRunSpecBrowserOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400 }}>
          {browserTocLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <CircularProgress />
            </Box>
          ) : browserToc.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No tables found for {selectedCustomer}/{selectedJob}
            </Typography>
          ) : (
            <Tree
              searchEnabled={true}
              nodes={browserToc}
              expanded={runSpecBrowserExpanded}
              selected=""
              onNodeExpandToggle={(itemId: string, node: GenNode, isExpanded: boolean) => {
                setRunSpecBrowserExpanded(prev =>
                  isExpanded
                    ? [...prev, itemId]
                    : prev.filter(id => id !== itemId)
                )
              }}
              onNodeSelectToggle={(itemId: string, node: GenNode, isSelected: boolean) => {
                if (isSelected && node.type === 'Table') {
                  const fullPath = getNodeID(node)
                  // Remove .cbt extension if present
                  setRunSpecTablePath(fullPath.replace(/\.cbt$/i, ''))
                  setRunSpecBrowserOpen(false)
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default Developer
